import React, { useState, useEffect, useRef } from 'react';
import { fetchStalls, addStall, editStall, deleteStall, sendStallReport } from '../api';
import { Store, Plus, Trash2, Loader2, AlertCircle, Image as ImageIcon, X, Edit3, Save, QrCode, Mail, Send, CheckCircle2, Info } from 'lucide-react';

export default function StallManager() {
  const [stalls, setStalls] = useState([]);
  const [newName, setNewName] = useState("");
  const [newImage, setNewImage] = useState(null);
  const [newEmail, setNewEmail] = useState("");
  const [newCanteenGroup, setNewCanteenGroup] = useState("College");
  const [editingStallId, setEditingStallId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const fileInputRef = useRef(null);

  // Custom Modal State
  const [modalState, setModalState] = useState({ isOpen: false, title: "", message: "", type: "success" });
  const showModal = (title, message, type = "success") => setModalState({ isOpen: true, title, message, type });
  const closeModal = () => setModalState({ ...modalState, isOpen: false });

  // Sudo Delete Modal State
  const [sudoDeleteModal, setSudoDeleteModal] = useState({ isOpen: false, stallId: null, stallName: "", inputPhrase: "" });

  const colors = {
    navy: '#0C2340', gold: '#E5A823', bg: '#F1F5F9',
    white: '#FFFFFF', text: '#1E293B', textMuted: '#64748B',
    border: '#E2E8F0', red: '#EF4444', blue: '#3B82F6'
  };

  useEffect(() => {
    loadStalls();
  }, []);

  const loadStalls = async () => {
    try {
      setLoading(true);
      const data = await fetchStalls();
      setStalls(data);
      setError("");
    } catch (err) {
      setError("Failed to load stalls.");
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const img = new Image();
        img.src = reader.result;
        img.onload = () => {
          const MAX_WIDTH = 600;
          let scaleSize = 1;
          if (img.width > MAX_WIDTH) scaleSize = MAX_WIDTH / img.width;
          const canvas = document.createElement("canvas");
          canvas.width = img.width * scaleSize;
          canvas.height = img.height * scaleSize;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          const compressedBase64 = canvas.toDataURL("image/jpeg", 0.7);
          setNewImage(compressedBase64);
        };
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;

    try {
      const isEditing = !!editingStallId;
      const existingStall = isEditing ? stalls.find(s => s.id === editingStallId) : null;
      const emailInput = newEmail.trim();
      // Check if it's a new email or if the email was changed during an update
      const emailChanged = isEditing ? (existingStall?.email !== emailInput) : !!emailInput;

      if (isEditing) {
        // Edit Existing
        const updated = await editStall(editingStallId, newName.trim(), newImage, emailInput, newCanteenGroup);
        setStalls(stalls.map(s => s.id === editingStallId ? updated : s));
      } else {
        // Add New
        const newStall = await addStall(newName.trim(), newImage, emailInput, newCanteenGroup);
        setStalls([...stalls, newStall]);
      }

      handleCancelEdit();
      setError("");

      const actionText = isEditing ? "updated" : "added";
      if (emailInput && emailChanged) {
        showModal("Success!", `Stall ${actionText} successfully! A verification email has been sent to ${emailInput}.`, "success");
      } else {
        showModal("Success!", `Stall ${actionText} successfully!`, "success");
      }
    } catch (err) {
      setError(err.message || "Operation failed.");
    }
  };

  const handleEditClick = (stall) => {
    setEditingStallId(stall.id);
    setNewName(stall.name);
    setNewImage(stall.image || null);
    setNewEmail(stall.email || "");
    setNewCanteenGroup(stall.canteen_group || "College");
    window.scrollTo({ top: 0, behavior: 'smooth' }); // Scroll up to the form
  };

  const handleCancelEdit = () => {
    setEditingStallId(null);
    setNewName("");
    setNewImage(null);
    setNewEmail("");
    setNewCanteenGroup("College");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleDownloadQR = (stallName) => {
    try {
      const safeStallName = encodeURIComponent(stallName);
      // Hardcode localhost base for development (or process.env in production)
      const targetUrl = `${window.location.origin}/?stall=${safeStallName}`;

      const qrApiUrl = `https://api.qrserver.com/v1/create-qr-code/?size=500x500&data=${encodeURIComponent(targetUrl)}&margin=20`;

      // Fetch the binary data, convert to a Blob so we can force a raw download instead of bouncing the browser tab
      fetch(qrApiUrl)
        .then(res => res.blob())
        .then(blob => {
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.style.display = 'none';
          a.href = url;
          a.download = `QR_${stallName.replace(/\s+/g, '_')}.png`;
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);
        })
        .catch(err => {
          showModal("Error", "Failed to generate QR. Ensure you have an active internet connection.", "error");
        });
    } catch (e) { }
  };

  const handleDelete = (id) => {
    const stall = stalls.find(s => s.id === id);
    if (!stall) return;
    setSudoDeleteModal({
      isOpen: true,
      stallId: id,
      stallName: stall.name,
      inputPhrase: ""
    });
  };

  const executeDeleteStall = async () => {
    const id = sudoDeleteModal.stallId;
    try {
      if (editingStallId === id) handleCancelEdit();
      await deleteStall(id);
      setStalls(stalls.filter(s => s.id !== id));
      setError("");
      setSudoDeleteModal({ isOpen: false, stallId: null, stallName: "", inputPhrase: "" });
      showModal("Success!", "Stall has been successfully removed.", "success");
    } catch (err) {
      setError("Failed to delete stall.");
      setSudoDeleteModal(prev => ({ ...prev, isOpen: false }));
      showModal("Error", "Failed to delete stall.", "error");
    }
  };

  const handleSendReport = async (id) => {
    try {
      showModal("Processing", "Generating and sending report... Please wait.", "info");
      await sendStallReport(id);
      showModal("Success!", "Report sent successfully!", "success");
    } catch (err) {
      showModal("Error", err.message || "Failed to send report.", "error");
    }
  };

  return (
    <div className="stall-manager-grid">
      {/* ─── RESPONSIVE LAYOUT STYLES ─── */}
      <style>{`
        .stall-manager-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 32px;
          align-items: start;
        }

        @media (max-width: 992px) {
          .stall-manager-grid {
            grid-template-columns: 1fr !important;
            gap: 24px !important;
          }
        }
      `}</style>

      {/* ─── LEFT COLUMN: FORM ─── */}
      <div style={{ backgroundColor: colors.white, borderRadius: '16px', padding: '32px', border: `1px solid ${colors.border}`, boxShadow: '0 4px 15px rgba(0,0,0,0.03)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px', borderBottom: `1px solid ${colors.border}`, paddingBottom: '20px' }}>
          <div style={{ backgroundColor: 'rgba(12, 35, 64, 0.05)', padding: '12px', borderRadius: '12px' }}>
            <Store size={28} color={colors.navy} />
          </div>
          <div>
            <h2 style={{ margin: '0 0 4px 0', fontSize: '22px', color: colors.navy, fontWeight: 800, letterSpacing: '-0.02em' }}>Stall Configurator</h2>
            <p style={{ margin: 0, fontSize: '14px', color: colors.textMuted }}>Add or edit individual food stalls.</p>
          </div>
        </div>

        {error && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', backgroundColor: '#FEF2F2', color: colors.red, padding: '16px', borderRadius: '8px', marginBottom: '24px', fontSize: '14px', fontWeight: 500, border: '1px solid #FECACA' }}>
            <AlertCircle size={18} /> {error}
          </div>
        )}

        {/* Form Context */}
        <div style={{ backgroundColor: editingStallId ? '#F0F9FF' : colors.bg, padding: '24px', borderRadius: '12px', border: `1px solid ${editingStallId ? '#BAE6FD' : colors.border}`, transition: 'all 0.3s' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h3 style={{ fontSize: '13px', fontWeight: 700, color: editingStallId ? colors.blue : colors.textMuted, letterSpacing: '0.05em', margin: 0 }}>
              {editingStallId ? 'EDITING STALL' : 'ADD NEW STALL'}
            </h3>
            {editingStallId && (
              <button onClick={handleCancelEdit} style={{ background: 'none', border: 'none', color: colors.textMuted, cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}>Cancel Edit</button>
            )}
          </div>

          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: colors.navy, marginBottom: '8px' }}>Stall Name <span style={{ color: colors.red }}>*</span></label>
              <input
                type="text"
                placeholder="e.g., Dad Bobs..."
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                style={{ width: '100%', padding: '14px 16px', borderRadius: '8px', border: `1px solid ${colors.border}`, fontSize: '15px', outline: 'none', boxSizing: 'border-box' }}
              />
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: colors.navy, marginBottom: '8px' }}>Stall Owner Email <span style={{ color: colors.red }}>*</span> <Mail size={14} style={{ display: 'inline', verticalAlign: 'middle', marginLeft: '4px' }} /></label>
              <input
                type="email"
                placeholder="owner@example.com"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                style={{ width: '100%', padding: '14px 16px', borderRadius: '8px', border: `1px solid ${colors.border}`, fontSize: '15px', outline: 'none', boxSizing: 'border-box' }}
              />
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: colors.navy, marginBottom: '8px' }}>Canteen Group <span style={{ color: colors.red }}>*</span></label>
              <select
                value={newCanteenGroup}
                onChange={(e) => setNewCanteenGroup(e.target.value)}
                style={{ width: '100%', padding: '14px 16px', borderRadius: '8px', border: `1px solid ${colors.border}`, fontSize: '15px', outline: 'none', backgroundColor: '#FFFFFF', boxSizing: 'border-box', fontFamily: 'inherit' }}
              >
                <option value="SHS">SHS</option>
                <option value="College">College</option>
              </select>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: colors.navy, marginBottom: '8px' }}>Cover Photo </label>
              <input type="file" accept="image/*" ref={fileInputRef} onChange={handleImageUpload} style={{ display: 'none' }} />

              {!newImage ? (
                <div
                  onClick={() => fileInputRef.current.click()}
                  style={{ width: '100%', padding: '20px', border: `2px dashed ${colors.border}`, borderRadius: '8px', backgroundColor: colors.white, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '12px', transition: 'all 0.2s', boxSizing: 'border-box' }}
                >
                  <div style={{ backgroundColor: colors.bg, padding: '8px', borderRadius: '50%' }}><ImageIcon size={20} color={colors.textMuted} /></div>
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <span style={{ fontSize: '14px', color: colors.text, fontWeight: 500 }}>Click to upload cover photo</span>
                    <span style={{ fontSize: '12px', color: colors.textMuted }}>Recommended ratio 16:9 (JPG/PNG)</span>
                  </div>
                </div>
              ) : (
                <div style={{ position: 'relative', width: '100%', height: '140px', borderRadius: '8px', overflow: 'hidden', border: `1px solid ${colors.border}` }}>
                  <img src={newImage} alt="Stall Preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  <button type="button" onClick={() => { setNewImage(null); if (fileInputRef.current) fileInputRef.current.value = ""; }} style={{ position: 'absolute', top: '8px', right: '8px', backgroundColor: 'rgba(0,0,0,0.6)', color: 'white', border: 'none', borderRadius: '50%', width: '28px', height: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                    <X size={14} />
                  </button>
                </div>
              )}
            </div>

            <button
              type="submit"
              disabled={!newName.trim()}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', backgroundColor: newName.trim() ? (editingStallId ? colors.blue : colors.navy) : '#94A3B8', color: colors.white, border: 'none', padding: '16px', borderRadius: '8px', fontSize: '15px', fontWeight: 600, cursor: newName.trim() ? 'pointer' : 'not-allowed', transition: 'all 0.2s', marginTop: '8px' }}
            >
              {editingStallId ? <><Save size={18} /> Update Stall</> : <><Plus size={18} /> Add Stall to Database</>}
            </button>
          </form>
        </div>
      </div>

      {/* ─── RIGHT COLUMN: ACTIVE STALLS ─── */}
      <div style={{ backgroundColor: colors.white, borderRadius: '16px', padding: '32px', border: `1px solid ${colors.border}`, boxShadow: '0 4px 15px rgba(0,0,0,0.03)' }}>
        <h3 style={{ fontSize: '14px', fontWeight: 700, color: colors.navy, letterSpacing: '0.05em', marginBottom: '24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: `1px solid ${colors.border}`, paddingBottom: '16px' }}>
          ACTIVE STALLS DIRECTORY
          <span style={{ backgroundColor: 'rgba(59, 130, 246, 0.1)', padding: '4px 10px', borderRadius: '12px', fontSize: '12px', color: colors.blue }}>{stalls.length} Total</span>
        </h3>

        {loading ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: colors.textMuted, fontSize: '14px', padding: '24px 0', justifyContent: 'center' }}>
            <Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} /> Loading...
          </div>
        ) : stalls.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', backgroundColor: colors.bg, borderRadius: '12px', border: `1px dashed ${colors.border}`, color: colors.textMuted, fontSize: '14px' }}>
            <Store size={32} color="#CBD5E1" style={{ margin: '0 auto 12px auto', display: 'block' }} />
            No stalls found. Add one above!
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {stalls.map(stall => (
              <div key={stall.id} style={{ display: 'flex', alignItems: 'center', padding: '16px', borderBottom: `1px solid ${colors.border}`, backgroundColor: editingStallId === stall.id ? '#F8FAFC' : 'transparent', gap: '16px', transition: 'background-color 0.2s' }} onMouseEnter={e => { if (editingStallId !== stall.id) e.currentTarget.style.backgroundColor = '#F8FAFC'; }} onMouseLeave={e => { if (editingStallId !== stall.id) e.currentTarget.style.backgroundColor = 'transparent'; }}>
                <div style={{ width: '48px', height: '48px', borderRadius: '8px', backgroundColor: colors.bg, overflow: 'hidden', flexShrink: 0 }}>
                  {stall.image ? (
                    <img
                      src={stall.image}
                      alt={stall.name}
                      crossOrigin="anonymous"
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  ) : (
                    <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Store size={20} color="#94A3B8" />
                    </div>
                  )}
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontWeight: 600, color: colors.text, fontSize: '15px' }}>{stall.name}</span>
                    {stall.canteen_group && (
                      <span style={{ 
                        fontSize: '10px', 
                        fontWeight: 700, 
                        color: stall.canteen_group === 'College' ? '#3B82F6' : '#EF4444', 
                        backgroundColor: stall.canteen_group === 'College' ? '#EFF6FF' : '#FEF2F2',
                        padding: '2px 6px', 
                        borderRadius: '4px',
                        border: `1px solid ${stall.canteen_group === 'College' ? '#BFDBFE' : '#FCA5A5'}`
                      }}>
                        {stall.canteen_group === 'College' ? 'College Canteen' : 'High School Canteen'}
                      </span>
                    )}
                  </div>
                  {stall.email && (
                    <span style={{ fontSize: '12px', color: stall.is_email_verified ? colors.blue : colors.textMuted, display: 'flex', alignItems: 'center', gap: '4px', marginTop: '2px' }}>
                      <Mail size={12} /> {stall.email} {stall.is_email_verified ? '(Verified)' : '(Unverified)'}
                    </span>
                  )}
                </div>

                <div style={{ display: 'flex', gap: '6px' }}>
                  {stall.is_email_verified && (
                    <button
                      onClick={() => handleSendReport(stall.id)}
                      style={{ background: '#ECFDF5', border: '1px solid #A7F3D0', color: '#059669', cursor: 'pointer', padding: '8px', borderRadius: '8px', display: 'flex', alignItems: 'center' }}
                      title="Send AI Report via Email"
                    >
                      <Send size={16} />
                    </button>
                  )}
                  <button
                    onClick={() => handleDownloadQR(stall.name)}
                    style={{ background: '#FAF5FF', border: '1px solid #E9D5FF', color: '#9333EA', cursor: 'pointer', padding: '8px', borderRadius: '8px', display: 'flex', alignItems: 'center' }}
                    title="Download Auto-Routing QR Code"
                  >
                    <QrCode size={16} />
                  </button>
                  <button
                    onClick={() => handleEditClick(stall)}
                    style={{ background: '#EFF6FF', border: '1px solid #BFDBFE', color: colors.blue, cursor: 'pointer', padding: '8px', borderRadius: '8px', display: 'flex', alignItems: 'center' }}
                    title="Edit Stall"
                  >
                    <Edit3 size={16} />
                  </button>
                  <button
                    onClick={() => handleDelete(stall.id)}
                    style={{ background: '#FEF2F2', border: '1px solid #FECACA', color: colors.red, cursor: 'pointer', padding: '8px', borderRadius: '8px', display: 'flex', alignItems: 'center' }}
                    title="Remove Stall"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ─── CUSTOM MODAL ─── */}
      {modalState.isOpen && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(12, 35, 64, 0.5)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
          <div style={{ backgroundColor: colors.white, padding: '32px', borderRadius: '16px', maxWidth: '420px', width: '90%', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)', transform: 'translateY(0)', animation: 'fadeUp 0.3s ease' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px' }}>
              {modalState.type === 'success' && <div style={{ backgroundColor: '#ECFDF5', padding: '12px', borderRadius: '50%' }}><CheckCircle2 color="#10B981" size={28} /></div>}
              {modalState.type === 'error' && <div style={{ backgroundColor: '#FEF2F2', padding: '12px', borderRadius: '50%' }}><AlertCircle color="#EF4444" size={28} /></div>}
              {modalState.type === 'info' && <div style={{ backgroundColor: '#EFF6FF', padding: '12px', borderRadius: '50%' }}><Info color="#3B82F6" size={28} /></div>}
              <h3 style={{ margin: 0, fontSize: '22px', color: colors.navy, fontWeight: 700 }}>{modalState.title}</h3>
            </div>
            <p style={{ margin: '0 0 28px 0', color: colors.textMuted, fontSize: '15px', lineHeight: 1.6 }}>
              {modalState.message}
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button onClick={closeModal} style={{ backgroundColor: colors.navy, color: colors.white, border: 'none', padding: '12px 24px', borderRadius: '8px', fontSize: '14px', fontWeight: 600, cursor: 'pointer', transition: 'background-color 0.2s' }} onMouseEnter={e => e.currentTarget.style.backgroundColor = '#17365C'} onMouseLeave={e => e.currentTarget.style.backgroundColor = colors.navy}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      {/* ─── SECURE SUDO DELETE MODAL ─── */}
      {sudoDeleteModal.isOpen && (() => {
        const requiredPhrase = `sudo-delete-${sudoDeleteModal.stallName.toLowerCase().replace(/\s+/g, '-')}`;
        const canDelete = sudoDeleteModal.inputPhrase === requiredPhrase;
        return (
          <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(12, 35, 64, 0.6)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 99999 }}>
            <div style={{ backgroundColor: colors.white, padding: '32px', borderRadius: '16px', maxWidth: '440px', width: '90%', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.2)', animation: 'fadeUp 0.3s ease' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px' }}>
                <div style={{ backgroundColor: '#FEF2F2', padding: '12px', borderRadius: '50%' }}><Trash2 color="#EF4444" size={28} /></div>
                <h3 style={{ margin: 0, fontSize: '20px', color: colors.navy, fontWeight: 700 }}>Confirm Deletion</h3>
              </div>

              <p style={{ margin: '0 0 16px 0', color: colors.textMuted, fontSize: '14px', lineHeight: 1.6 }}>
                Are you sure you want to delete <strong style={{ color: colors.navy }}>{sudoDeleteModal.stallName}</strong>? This will permanently delete the stall cover image, PDF reports, and all related student feedback data.
              </p>

              <div style={{ marginBottom: '24px', backgroundColor: colors.bg, padding: '16px', borderRadius: '8px', border: `1px solid ${colors.border}` }}>
                <div style={{ fontSize: '12px', color: colors.textMuted, marginBottom: '6px', fontWeight: 600 }}>REQUIRED VERIFICATION CODE:</div>
                <code style={{ fontSize: '14px', fontWeight: 'bold', color: colors.red, wordBreak: 'break-all', fontFamily: 'monospace' }}>{requiredPhrase}</code>
              </div>

              <div style={{ marginBottom: '28px' }}>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: colors.textMuted, marginBottom: '8px' }}>TYPE CODE TO PROCEED</label>
                <input 
                  type="text" 
                  placeholder={requiredPhrase}
                  value={sudoDeleteModal.inputPhrase}
                  onChange={(e) => setSudoDeleteModal(prev => ({ ...prev, inputPhrase: e.target.value }))}
                  style={{ width: '100%', padding: '12px 14px', borderRadius: '8px', border: `1px solid ${canDelete ? '#10B981' : colors.border}`, outline: 'none', fontSize: '14px', color: colors.text, boxSizing: 'border-box' }}
                />
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                <button 
                  onClick={() => setSudoDeleteModal({ isOpen: false, stallId: null, stallName: "", inputPhrase: "" })} 
                  style={{ backgroundColor: 'transparent', border: `1px solid ${colors.border}`, color: colors.textMuted, padding: '10px 20px', borderRadius: '8px', fontSize: '14px', fontWeight: 600, cursor: 'pointer' }}
                >
                  Cancel
                </button>
                <button 
                  disabled={!canDelete}
                  onClick={executeDeleteStall} 
                  style={{ backgroundColor: canDelete ? colors.red : '#CBD5E1', color: colors.white, border: 'none', padding: '10px 20px', borderRadius: '8px', fontSize: '14px', fontWeight: 600, cursor: canDelete ? 'pointer' : 'not-allowed', transition: 'all 0.2s' }}
                >
                  Permanently Delete
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}